import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import './SuperAdminTenantSelector.css';

const SuperAdminTenantSelector = ({ onTenantSelected, selectedTenantId }) => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('organizations'); // organizations | users

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tenantsRes, usersRes] = await Promise.all([
          apiClient.get('/api/super-admin/tenants'),
          apiClient.get('/api/super-admin/users')
        ]);
        setTenants(tenantsRes.data.tenants);
        setStats(tenantsRes.data.stats);
        setUsers(usersRes.data.users);
      } catch (error) {
        console.error('Erreur chargement tenants/users:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Préparation des listes pour chaque tab
  // 1. Liste des organisations (avec recherche croisée user principal)
  const filteredTenants = tenants.filter(tenant => {
    const search = searchTerm.toLowerCase();
    const owner = tenant.owner || {};
    return (
      tenant.name.toLowerCase().includes(search) ||
      tenant.tenantId.toLowerCase().includes(search) ||
      tenant.ownerEmail?.toLowerCase().includes(search) ||
      owner.email?.toLowerCase().includes(search) ||
      owner.firstName?.toLowerCase().includes(search) ||
      owner.lastName?.toLowerCase().includes(search)
    );
  });

  // 2. Liste de tous les users (full MongoDB)
  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    return (
      (user.firstName?.toLowerCase().includes(search) || '') ||
      (user.lastName?.toLowerCase().includes(search) || '') ||
      (user.email?.toLowerCase().includes(search) || '') ||
      (user.organization?.toLowerCase().includes(search) || '') ||
      (user.tenantId?.toLowerCase().includes(search) || '')
    );
  });

  // Pagination
  const paginatedTenants = filteredTenants.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const paginatedUsers = filteredUsers.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = activeTab === 'organizations'
    ? Math.ceil(filteredTenants.length / rowsPerPage)
    : Math.ceil(filteredUsers.length / rowsPerPage);

  const handleTenantSelect = async (tenant) => {
    onTenantSelected(tenant);
    
    // Charger les stats du tenant sélectionné
    try {
      const response = await apiClient.get(`/api/super-admin/tenants/${tenant.tenantId}/stats`);
      setStats(prev => ({
        ...prev,
        selected: response.data
      }));
    } catch (error) {
      console.error('Erreur stats tenant:', error);
    }
  };

  if (loading) {
    return (
      <div className="super-admin-tenant-selector">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-tenant-selector">
      {/* Onglets de filtrage */}
      <div className="tab-container">
        <button className={activeTab === 'organizations' ? 'active' : ''} onClick={() => { setActiveTab('organizations'); setPage(1); }}>
          🏢 Organisations
        </button>
        <button className={activeTab === 'users' ? 'active' : ''} onClick={() => { setActiveTab('users'); setPage(1); }}>
          👥 Utilisateurs
        </button>
      </div>

      {/* Header avec stats globales */}
      <div className="tenant-selector-header">
        <h2>🏢 Support Multi-Tenant</h2>
        <div className="global-stats">
          <div className="stat-card">
            <span className="stat-number">{stats.totalTenants || 0}</span>
            <span className="stat-label">🏢 Organisations</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{stats.totalUsers || 0}</span>
            <span className="stat-label">👥 Utilisateurs</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{stats.totalNodes || 0}</span>
            <span className="stat-label">🔗 Nœuds</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{stats.activeToday || 0}</span>
            <span className="stat-label">⚡ Actifs aujourd'hui</span>
          </div>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="tenant-search">
        <input
          type="text"
          placeholder={activeTab === 'organizations' ? "🔍 Rechercher par organisation, tenant ID, email ou utilisateur..." : "🔍 Rechercher par utilisateur, email, organisation..."}
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          className="tenant-search-input"
        />
      </div>

      {/* Tableau paginé selon l'onglet actif */}
      <div className="tenants-table-container">
        {activeTab === 'organizations' ? (
          <table className="tenants-table">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>Tenant ID</th>
                <th>Plan</th>
                <th>Statut</th>
                <th>Créé</th>
                <th>Propriétaire</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTenants.map(tenant => (
                <tr key={tenant.tenantId} className={selectedTenantId === tenant.tenantId ? 'selected-row' : ''}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="user-avatar">
                        {tenant.name.charAt(0).toUpperCase()}
                      </div>
                      <strong>{tenant.name}</strong>
                    </div>
                  </td>
                  <td>
                    <code>{tenant.tenantId}</code>
                  </td>
                  <td>
                    <span className={`plan-badge ${tenant.plan}`}>
                      {tenant.plan}
                    </span>
                  </td>
                  <td>
                    <span className={`status-indicator ${tenant.status === 'active' ? 'active' : 'inactive'}`}>
                      {tenant.status === 'active' ? '🟢' : '🔴'}
                      {tenant.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>{new Date(tenant.createdAt).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="user-avatar" style={{ width: '24px', height: '24px', fontSize: '10px' }}>
                        {(tenant.owner?.firstName || tenant.ownerEmail || 'U').charAt(0).toUpperCase()}
                      </div>
                      {tenant.owner?.firstName || ''} {tenant.owner?.lastName || ''}
                    </div>
                  </td>
                  <td>{tenant.owner?.email || tenant.ownerEmail}</td>
                  <td>
                    <span className={`role-badge ${(tenant.owner?.role || 'admin').replace('-', '')}`}>
                      {tenant.owner?.role || 'admin'}
                    </span>
                  </td>
                  <td>
                    <button className="action-btn primary" onClick={() => handleTenantSelect(tenant)}>
                      🔍 Support
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="tenants-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Organisation</th>
                <th>Tenant ID</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map(user => (
                <tr key={user.email + user.tenantId}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="user-avatar">
                        {(user.firstName || user.email).charAt(0).toUpperCase()}
                      </div>
                      <strong>{user.firstName || ''} {user.lastName || ''}</strong>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${(user.role || 'user').replace('-', '')}`}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="user-avatar" style={{ width: '24px', height: '24px', fontSize: '10px' }}>
                        {(user.organization || 'O').charAt(0).toUpperCase()}
                      </div>
                      {user.organization || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <code>{user.tenantId}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            {activeTab === 'organizations' ? (
              <span>Affichage de {(page - 1) * rowsPerPage + 1} à {Math.min(page * rowsPerPage, filteredTenants.length)} sur {filteredTenants.length} organisations</span>
            ) : (
              <span>Affichage de {(page - 1) * rowsPerPage + 1} à {Math.min(page * rowsPerPage, filteredUsers.length)} sur {filteredUsers.length} utilisateurs</span>
            )}
          </div>
          <div className="pagination-controls">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              ⬅️ Précédent
            </button>
            <span className="page-info">Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              Suivant ➡️
            </button>
            <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={20}>20 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Message si pas de résultats */}
      {((activeTab === 'organizations' && filteredTenants.length === 0) || (activeTab === 'users' && filteredUsers.length === 0)) && (
        <div className="no-results">
          <p>🔍 {t('superAdminTenantSelector.noResults', { searchTerm })}</p>
        </div>
      )}
    </div>
  );
};

export default SuperAdminTenantSelector;
